import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  UploadCloud,
  FileText,
  CheckCircle2,
  Clock,
  AlertTriangle,
  Download,
  Trash2,
  Plus,
  X,
  FileCheck,
  ShieldCheck,
  FolderOpen,
  Info,
  Loader2,
  Users,
  User,
  Heart,
  Baby,
  Filter,
  UserCheck,
  HeartHandshake,
  Sparkles,
} from "lucide-react";
import { api, API_BASE_URL } from "../lib/api";
import { useLang } from "../context/LanguageContext";

// Base categories configuration
const ALL_CATEGORIES = [
  {
    id: "passport",
    fr: "Passeport(s) international(aux)",
    en: "International Passport(s)",
    solo_fr: "Votre page biométrique avec photo et date de validité",
    solo_en: "Your biographical page with photo and validity date",
    couple_fr: "Passeport du demandeur principal et de son/sa conjoint(e)",
    couple_en: "Passports for principal applicant and spouse",
    family_fr: "Passeports de tous les membres (demandeur, conjoint, chaque enfant)",
    family_en: "Passports for all members (principal, spouse, each child)",
    forSituations: ["single", "couple", "family"],
  },
  {
    id: "birth_certificate",
    fr: "Acte(s) de naissance & Filiation",
    en: "Birth Certificate(s) & Lineage",
    solo_fr: "Votre copie intégrale d'acte de naissance officielle et légalisée",
    solo_en: "Your certified official birth certificate",
    couple_fr: "Actes de naissance du demandeur et du/de la conjoint(e)",
    couple_en: "Birth certificates for applicant and spouse",
    family_fr: "Actes de naissance de tous les membres de la famille avec filiation",
    family_en: "Full birth certificates for all family members showing parents' names",
    forSituations: ["single", "couple", "family"],
  },
  {
    id: "marriage_certificate",
    fr: "Acte de mariage / Preuve d'union",
    en: "Marriage Certificate / Common-Law",
    solo_fr: "Non requis pour candidat célibataire",
    solo_en: "Not required for single applicant",
    couple_fr: "Certificat de mariage officiel ou preuves d'union de fait (bail commun, comptes joints)",
    couple_en: "Official marriage certificate or common-law proof (joint lease, bills)",
    family_fr: "Certificat de mariage officiel ou preuves d'union de fait",
    family_en: "Official marriage certificate or common-law proof",
    forSituations: ["couple", "family"],
  },
  {
    id: "tcf_results",
    fr: "Attestation TCF / TEF Canada",
    en: "TCF / TEF Canada Test Report",
    solo_fr: "Votre attestation officielle avec niveau NCLC (Compréhension & Expression)",
    solo_en: "Your official test report with CLB scores across all 4 abilities",
    couple_fr: "Attestations TCF/TEF du demandeur et du conjoint (si applicable)",
    couple_en: "Language test reports for principal applicant and spouse (if applicable)",
    family_fr: "Attestations TCF/TEF du demandeur et du conjoint (si applicable)",
    family_en: "Language test reports for principal applicant and spouse (if applicable)",
    forSituations: ["single", "couple", "family"],
  },
  {
    id: "wes_report",
    fr: "Rapport d'évaluation WES / EDE",
    en: "WES / ECA Credential Assessment",
    solo_fr: "Votre rapport officiel d'évaluation des diplômes d'études (EDE) IRCC",
    solo_en: "Your official IRCC Educational Credential Assessment (ECA) report",
    couple_fr: "Rapports WES / EDE du demandeur et du/de la conjoint(e)",
    couple_en: "WES / ECA reports for principal applicant and spouse",
    family_fr: "Rapports WES / EDE du demandeur et du/de la conjoint(e)",
    family_en: "WES / ECA reports for principal applicant and spouse",
    forSituations: ["single", "couple", "family"],
  },
  {
    id: "diplomas",
    fr: "Diplômes & Relevés de notes",
    en: "Diplomas & Transcripts",
    solo_fr: "Vos diplômes universitaires, baccalauréat et relevés annuels",
    solo_en: "Your post-secondary degrees, high school diploma, and annual transcripts",
    couple_fr: "Diplômes et relevés du demandeur et du/de la conjoint(e)",
    couple_en: "Diplomas and transcripts for both applicant and spouse",
    family_fr: "Diplômes et relevés du demandeur et du/de la conjoint(e)",
    family_en: "Diplomas and transcripts for both applicant and spouse",
    forSituations: ["single", "couple", "family"],
  },
  {
    id: "work_experience",
    fr: "Attestations de travail, Contrats & CV",
    en: "Work Certificates, Contracts & Resume",
    solo_fr: "Vos attestations d'emploi détaillées, fiches de paie et CV actualisé",
    solo_en: "Your detailed work experience letters, pay slips, and updated CV",
    couple_fr: "Preuves d'expérience professionnelle du demandeur (et conjoint si pertinent)",
    couple_en: "Work experience proofs for applicant (and spouse if applicable)",
    family_fr: "Preuves d'expérience professionnelle du demandeur (et conjoint si pertinent)",
    family_en: "Work experience proofs for applicant (and spouse if applicable)",
    forSituations: ["single", "couple", "family"],
  },
  {
    id: "police_clearance",
    fr: "Casier judiciaire / Certificat de police",
    en: "Police Clearance Certificate",
    solo_fr: "Votre extrait de casier judiciaire récent de chaque pays habité 6+ mois",
    solo_en: "Your recent police certificate from every country lived 6+ months",
    couple_fr: "Casiers judiciaires pour le demandeur et le/la conjoint(e)",
    couple_en: "Police certificates for applicant and spouse",
    family_fr: "Casiers judiciaires pour le demandeur et le/la conjoint(e) (et enfants de 18+ ans)",
    family_en: "Police certificates for applicant, spouse (and children aged 18+)",
    forSituations: ["single", "couple", "family"],
  },
  {
    id: "medical_exam",
    fr: "Visite médicale préalable (E-Medical)",
    en: "Upfront Medical Exam (E-Medical)",
    solo_fr: "Feuille d'information E-Medical remise par le médecin désigné IRCC",
    solo_en: "E-Medical information sheet issued by your IRCC panel physician",
    couple_fr: "Feuilles E-Medical pour le demandeur et le/la conjoint(e)",
    couple_en: "E-Medical sheets for applicant and spouse",
    family_fr: "Feuilles E-Medical pour tous les membres de la famille examinés",
    family_en: "E-Medical sheets for all examined family members",
    forSituations: ["single", "couple", "family"],
  },
  {
    id: "proof_of_funds",
    fr: "Preuve de fonds & Relevés bancaires",
    en: "Proof of Settlement Funds",
    solo_fr: "Attestation bancaire officielle (seuil 1 personne IRCC ~14 690 $ CAD) et 6 mois de relevés",
    solo_en: "Official bank letter (1-person IRCC threshold ~14,690 $ CAD) & 6-month statements",
    couple_fr: "Attestation bancaire officielle (seuil 2 personnes IRCC ~18 288 $ CAD) et 6 mois de relevés",
    couple_en: "Official bank letter (2-person IRCC threshold ~18,288 $ CAD) & 6-month statements",
    family_fr: "Attestation bancaire officielle (seuil famille 3+ personnes IRCC) et 6 mois de relevés",
    family_en: "Official bank letter matching your total family size threshold & 6-month statements",
    forSituations: ["single", "couple", "family"],
  },
  {
    id: "children_docs",
    fr: "Documents Enfants (Scolarité / Garde)",
    en: "Children Documents (School / Custody)",
    solo_fr: "Non applicable",
    solo_en: "Not applicable",
    couple_fr: "Non applicable",
    couple_en: "Not applicable",
    family_fr: "Certificats de scolarité, autorisations de voyage de l'autre parent ou jugements de garde",
    family_en: "School enrollment certificates, parental travel authorization or custody records",
    forSituations: ["family"],
  },
  {
    id: "other",
    fr: "Autre document justificatif",
    en: "Other Supporting Document",
    solo_fr: "Photos d'identité IRCC, explications ou formulaires annexes",
    solo_en: "IRCC compliant photos, client explanatory notes or extra forms",
    couple_fr: "Photos IRCC, justificatifs annexes pour le couple",
    couple_en: "IRCC photos, supplemental supporting documents for the couple",
    family_fr: "Photos IRCC des membres, formulaires familiaux ou lettres explicatives",
    family_en: "Family members' IRCC photos, additional family forms, or letters",
    forSituations: ["single", "couple", "family"],
  },
];

const FAMILY_ROLES = [
  { id: "main_applicant", fr: "Demandeur Principal", en: "Principal Applicant", icon: User, color: "text-blue-700 bg-blue-50 border-blue-200" },
  { id: "spouse", fr: "Conjoint(e) / Époux(se)", en: "Spouse / Partner", icon: Heart, color: "text-purple-700 bg-purple-50 border-purple-200" },
  { id: "child", fr: "Enfant à charge", en: "Dependent Child", icon: Baby, color: "text-amber-700 bg-amber-50 border-amber-200" },
  { id: "other", fr: "Autre membre", en: "Other Member", icon: Users, color: "text-slate-700 bg-slate-50 border-slate-200" },
];

export default function ClientDocumentsPortal() {
  const { lang } = useLang();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Situation State: 'single' (Seul) | 'couple' (En couple / Marié) | 'family' (Famille avec enfants)
  const [familySituation, setFamilySituation] = useState(() => {
    return localStorage.getItem("jnic_family_situation") || "single";
  });

  const [uploadModalCategory, setUploadModalCategory] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [customTitle, setCustomTitle] = useState("");
  const [familyMember, setFamilyMember] = useState("main_applicant");
  const [familyMemberName, setFamilyMemberName] = useState("");
  const [familyFilter, setFamilyFilter] = useState("all");
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [uploadSuccess, setUploadSuccess] = useState("");
  const [deletingId, setDeletingId] = useState(null);
  const fileInputRef = useRef(null);

  const handleSituationChange = (newSituation) => {
    setFamilySituation(newSituation);
    localStorage.setItem("jnic_family_situation", newSituation);
    // Reset family filter when changing situation
    setFamilyFilter("all");
  };

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const res = await api.get("/documents/my-documents");
      const docs = Array.isArray(res.data) ? res.data : [];
      setDocuments(docs);
      
      // Auto-detect situation if user already has spouse or child documents
      const hasChildDoc = docs.some((d) => d.family_member === "child" || d.document_type === "children_docs");
      const hasSpouseDoc = docs.some((d) => d.family_member === "spouse" || d.document_type === "marriage_certificate");
      
      if (!localStorage.getItem("jnic_family_situation")) {
        if (hasChildDoc) {
          setFamilySituation("family");
        } else if (hasSpouseDoc) {
          setFamilySituation("couple");
        }
      }
    } catch (err) {
      console.error("Failed to load documents:", err);
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, []);

  const handleOpenUpload = (catId) => {
    setUploadModalCategory(catId);
    setSelectedFile(null);
    setCustomTitle("");
    
    // Auto-set sensible family member default according to category
    if (catId === "marriage_certificate") {
      setFamilyMember("spouse");
    } else if (catId === "children_docs") {
      setFamilyMember("child");
    } else {
      setFamilyMember("main_applicant");
    }
    
    setFamilyMemberName("");
    setUploadError("");
    setUploadSuccess("");
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 15 * 1024 * 1024) {
        setUploadError(lang === "fr" ? "Le fichier dépasse la taille maximale autorisée (15 Mo)." : "File exceeds maximum size limit (15 MB).");
        return;
      }
      setSelectedFile(file);
      setUploadError("");
      if (!customTitle) {
        setCustomTitle(file.name.replace(/\.[^/.]+$/, ""));
      }
    }
  };

  const handleUploadSubmit = async (e) => {
    e.preventDefault();
    if (!selectedFile || !uploadModalCategory) {
      setUploadError(lang === "fr" ? "Veuillez sélectionner un fichier." : "Please select a file.");
      return;
    }

    try {
      setUploading(true);
      setUploadError("");

      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("document_type", uploadModalCategory);
      
      // If single, it's always main applicant
      const targetMember = familySituation === "single" ? "main_applicant" : familyMember;
      formData.append("family_member", targetMember);
      
      if (familyMemberName.trim()) {
        formData.append("family_member_name", familyMemberName.trim());
      }
      if (customTitle.trim()) {
        formData.append("custom_title", customTitle.trim());
      }

      const res = await api.post("/documents/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const uploadedDoc = res.data?.document || (res.data?.doc_id ? res.data : null);
      if (uploadedDoc) {
        setDocuments((prev) => [uploadedDoc, ...prev]);
      }

      setUploadSuccess(lang === "fr" ? "Document téléversé avec succès !" : "Document successfully uploaded!");
      setTimeout(() => {
        setUploadModalCategory(null);
        setSelectedFile(null);
        setCustomTitle("");
        setFamilyMemberName("");
        setUploadSuccess("");
        fetchDocuments();
      }, 500);
    } catch (err) {
      console.error("Upload error:", err);
      const msg = err.response?.data?.detail || (lang === "fr" ? "Erreur lors du téléversement. Assurez-vous d'être connecté." : "Upload failed. Please ensure you are logged in.");
      setUploadError(msg);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm(lang === "fr" ? "Voulez-vous vraiment supprimer ce document ?" : "Are you sure you want to delete this document?")) {
      return;
    }
    try {
      setDeletingId(docId);
      await api.delete(`/documents/${docId}`);
      setDocuments((prev) => prev.filter((d) => d.doc_id !== docId));
    } catch (err) {
      alert(lang === "fr" ? "Erreur lors de la suppression." : "Deletion failed.");
    } finally {
      setDeletingId(null);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "0 Ko";
    const k = 1024;
    const dm = 1;
    const sizes = ["Octets", "Ko", "Mo", "Go"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
  };

  const getFamilyBadge = (doc) => {
    if (familySituation === "single") return null;
    const role = FAMILY_ROLES.find((r) => r.id === (doc.family_member || "main_applicant")) || FAMILY_ROLES[0];
    const Icon = role.icon;
    const nameExtra = doc.family_member_name ? ` · ${doc.family_member_name}` : "";
    return (
      <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-md border ${role.color}`}>
        <Icon size={11} />
        <span>{lang === "fr" ? role.fr : role.en}{nameExtra}</span>
      </span>
    );
  };

  // Filter categories according to chosen situation
  const visibleCategories = ALL_CATEGORIES.filter((cat) =>
    cat.forSituations.includes(familySituation)
  );

  // Filter documents by family member (only if not single)
  const filteredDocuments = documents.filter((d) => {
    if (familySituation === "single") return true;
    if (familyFilter === "all") return true;
    return (d.family_member || "main_applicant") === familyFilter;
  });

  // Group docs by category
  const docsByCategory = {};
  visibleCategories.forEach((c) => {
    docsByCategory[c.id] = filteredDocuments.filter((d) => d.document_type === c.id);
  });

  const totalUploaded = documents.length;
  const approvedCount = documents.filter((d) => d.status === "approved").length;
  const actionRequiredCount = documents.filter((d) => d.status === "action_required").length;

  // Description helper depending on active situation
  const getCatDescription = (cat) => {
    if (familySituation === "single") {
      return lang === "fr" ? cat.solo_fr : cat.solo_en;
    } else if (familySituation === "couple") {
      return lang === "fr" ? cat.couple_fr : cat.couple_en;
    } else {
      return lang === "fr" ? cat.family_fr : cat.family_en;
    }
  };

  return (
    <div className="space-y-8" data-testid="client-documents-portal">
      {/* 1. Situation Selector Bar (Seul / En Couple / En Famille) */}
      <div className="bg-white rounded-2xl border border-navy/15 p-4 sm:p-5 shadow-xs">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-xs font-bold text-terracotta uppercase tracking-wider mb-1">
              <Sparkles size={14} />
              <span>{lang === "fr" ? "Structure de votre demande d'immigration" : "Your Immigration Application Setup"}</span>
            </div>
            <h3 className="text-base sm:text-lg font-serif font-bold text-navy">
              {lang === "fr" ? "Quel est votre profil d'accompagnement ?" : "What is your application profile?"}
            </h3>
            <p className="text-xs text-navy/60">
              {lang === "fr"
                ? "L'interface adapte automatiquement la liste des pièces et les exigences financières."
                : "The portal automatically adapts the document checklist and financial requirements."}
            </p>
          </div>

          <div className="grid grid-cols-3 gap-2 bg-alabaster p-1.5 rounded-2xl border border-navy/10 min-w-[320px] sm:min-w-[400px]">
            {/* Solo */}
            <button
              type="button"
              onClick={() => handleSituationChange("single")}
              className={`flex flex-col sm:flex-row items-center justify-center gap-1.5 py-2.5 px-3 rounded-xl text-xs font-semibold transition-all ${
                familySituation === "single"
                  ? "bg-white text-navy shadow-xs border border-navy/15 font-bold"
                  : "text-navy/60 hover:text-navy hover:bg-white/50"
              }`}
            >
              <User size={15} className={familySituation === "single" ? "text-terracotta" : "text-navy/40"} />
              <span>{lang === "fr" ? "Candidat Seul" : "Single Applicant"}</span>
            </button>

            {/* Couple */}
            <button
              type="button"
              onClick={() => handleSituationChange("couple")}
              className={`flex flex-col sm:flex-row items-center justify-center gap-1.5 py-2.5 px-3 rounded-xl text-xs font-semibold transition-all ${
                familySituation === "couple"
                  ? "bg-white text-navy shadow-xs border border-navy/15 font-bold"
                  : "text-navy/60 hover:text-navy hover:bg-white/50"
              }`}
            >
              <Heart size={15} className={familySituation === "couple" ? "text-terracotta" : "text-navy/40"} />
              <span>{lang === "fr" ? "En Couple" : "Couple"}</span>
            </button>

            {/* Famille avec enfants */}
            <button
              type="button"
              onClick={() => handleSituationChange("family")}
              className={`flex flex-col sm:flex-row items-center justify-center gap-1.5 py-2.5 px-3 rounded-xl text-xs font-semibold transition-all ${
                familySituation === "family"
                  ? "bg-white text-navy shadow-xs border border-navy/15 font-bold"
                  : "text-navy/60 hover:text-navy hover:bg-white/50"
              }`}
            >
              <Baby size={15} className={familySituation === "family" ? "text-terracotta" : "text-navy/40"} />
              <span>{lang === "fr" ? "Avec Enfants" : "With Children"}</span>
            </button>
          </div>
        </div>
      </div>

      {/* 2. Header Banner Adaptif */}
      <div className="bg-gradient-to-br from-navy to-[#0F2942] text-white p-6 sm:p-8 rounded-3xl shadow-sm border border-navy/10 relative overflow-hidden">
        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-terracotta text-xs font-semibold uppercase tracking-wider mb-3">
              <FolderOpen size={14} />
              <span>
                {familySituation === "single"
                  ? lang === "fr" ? "Dossier Individuel" : "Individual File"
                  : familySituation === "couple"
                  ? lang === "fr" ? "Dossier Couple & Conjoint" : "Couple Application File"
                  : lang === "fr" ? "Dossier Familial & Enfants" : "Family & Children File"}
              </span>
            </div>
            <h2 className="font-serif text-2xl sm:text-3xl font-bold tracking-tight">
              {familySituation === "single"
                ? lang === "fr" ? "Mes Pièces Justificatives (Dossier Personnel)" : "My Supporting Documents (Personal File)"
                : familySituation === "couple"
                ? lang === "fr" ? "Pièces Justificatives (Demandeur & Conjoint)" : "Supporting Documents (Applicant & Spouse)"
                : lang === "fr" ? "Pièces Justificatives (Demandeur, Conjoint & Enfants)" : "Supporting Documents (Applicant, Spouse & Children)"}
            </h2>
            <p className="text-white/80 text-sm mt-1 max-w-xl">
              {familySituation === "single"
                ? lang === "fr"
                  ? "Téléversez vos documents par catégorie. Les pièces sont vérifiées directement par nos experts réglementés JNIC."
                  : "Upload your personal documents by category. Files are verified by our regulated JNIC consultants."
                : lang === "fr"
                ? "Téléversez les documents requis pour chaque membre de votre famille. Vous pouvez ajouter autant de fichiers que nécessaire."
                : "Upload documents for each family member. You can upload multiple files under each required category."}
            </p>
          </div>

          <div className="grid grid-cols-3 gap-3 sm:gap-4 bg-white/10 backdrop-blur-md p-4 rounded-2xl border border-white/10 text-center min-w-[280px]">
            <div>
              <p className="text-2xl font-bold font-serif text-white">{totalUploaded}</p>
              <p className="text-[11px] uppercase tracking-wider text-white/70">{lang === "fr" ? "Téléversés" : "Uploaded"}</p>
            </div>
            <div className="border-x border-white/10 px-2">
              <p className="text-2xl font-bold font-serif text-emerald-400">{approvedCount}</p>
              <p className="text-[11px] uppercase tracking-wider text-white/70">{lang === "fr" ? "Validés" : "Approved"}</p>
            </div>
            <div>
              <p className="text-2xl font-bold font-serif text-amber-400">{actionRequiredCount}</p>
              <p className="text-[11px] uppercase tracking-wider text-white/70">{lang === "fr" ? "À revoir" : "Action req."}</p>
            </div>
          </div>
        </div>
      </div>

      {/* 3. Family Member Filter Pills (Only shown if Couple or Family) */}
      {familySituation !== "single" && (
        <div className="flex items-center gap-2 overflow-x-auto pb-2">
          <div className="flex items-center gap-1.5 text-xs text-navy/60 font-semibold uppercase tracking-wider pr-2 shrink-0">
            <Filter size={14} />
            <span>{lang === "fr" ? "Filtrer l'affichage par membre :" : "Filter view by member:"}</span>
          </div>
          <button
            type="button"
            onClick={() => setFamilyFilter("all")}
            className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition shrink-0 ${
              familyFilter === "all"
                ? "bg-navy text-white shadow-xs"
                : "bg-white text-navy/70 border border-navy/15 hover:bg-navy/5"
            }`}
          >
            {familySituation === "family" ? (lang === "fr" ? "👨‍👩‍👧‍👦 Toute la famille" : "👨‍👩‍👧‍👦 Entire Family") : (lang === "fr" ? "👫 Le couple" : "👫 Couple")} ({documents.length})
          </button>
          {FAMILY_ROLES.filter((role) => (familySituation === "couple" ? role.id === "main_applicant" || role.id === "spouse" : true)).map((role) => {
            const count = documents.filter((d) => (d.family_member || "main_applicant") === role.id).length;
            const Icon = role.icon;
            return (
              <button
                key={role.id}
                type="button"
                onClick={() => setFamilyFilter(role.id)}
                className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-semibold transition shrink-0 ${
                  familyFilter === role.id
                    ? "bg-navy text-white shadow-xs"
                    : "bg-white text-navy/70 border border-navy/15 hover:bg-navy/5"
                }`}
              >
                <Icon size={13} />
                <span>{lang === "fr" ? role.fr : role.en}</span>
                <span className="opacity-70 font-mono text-[11px]">({count})</span>
              </button>
            );
          })}
        </div>
      )}

      {/* 4. Adaptive Categories Checklist */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {visibleCategories.map((cat) => {
          const catDocs = docsByCategory[cat.id] || [];
          const hasApproved = catDocs.some((d) => d.status === "approved");
          const hasActionRequired = catDocs.some((d) => d.status === "action_required");

          return (
            <motion.div
              key={cat.id}
              layout
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              className={`bg-white rounded-2xl border transition-all duration-200 p-5 flex flex-col justify-between shadow-xs ${
                hasActionRequired
                  ? "border-amber-400/80 bg-amber-50/20 ring-1 ring-amber-400/30"
                  : catDocs.length > 0
                  ? "border-navy/20 hover:border-navy/35"
                  : "border-navy/10 hover:border-navy/25"
              }`}
            >
              <div>
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="flex items-center gap-2">
                    <span className="p-2 rounded-lg bg-navy/5 text-navy">
                      <FileText size={18} />
                    </span>
                    <h3 className="font-semibold text-navy text-base leading-snug">
                      {lang === "fr" ? cat.fr : cat.en}
                    </h3>
                  </div>
                  {catDocs.length > 0 ? (
                    hasApproved ? (
                      <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200">
                        <CheckCircle2 size={12} />
                        {lang === "fr" ? "Validé" : "Approved"}
                      </span>
                    ) : hasActionRequired ? (
                      <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full border border-amber-200">
                        <AlertTriangle size={12} />
                        {lang === "fr" ? "À corriger" : "Needs fix"}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-blue-700 bg-blue-50 px-2 py-0.5 rounded-full border border-blue-200">
                        <Clock size={12} />
                        {lang === "fr" ? "En révision" : "Under review"}
                      </span>
                    )
                  ) : (
                    <span className="text-[11px] text-navy/40 font-medium bg-navy/5 px-2 py-0.5 rounded-full">
                      {lang === "fr" ? "En attente" : "Pending"}
                    </span>
                  )}
                </div>

                <p className="text-xs text-navy/60 mb-4 pl-9">
                  {getCatDescription(cat)}
                </p>

                {/* Uploaded files under this category */}
                {catDocs.length > 0 && (
                  <div className="space-y-2.5 mb-4">
                    {catDocs.map((doc) => (
                      <div
                        key={doc.doc_id}
                        className="bg-navy/[0.03] border border-navy/10 rounded-xl p-3 text-xs space-y-2"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            {getFamilyBadge(doc) && <div className="mb-1.5">{getFamilyBadge(doc)}</div>}
                            <p className="font-medium text-navy truncate text-xs" title={doc.custom_title || doc.original_filename}>
                              {doc.custom_title || doc.original_filename}
                            </p>
                            <p className="text-[10px] text-navy/50 mt-0.5">
                              {formatFileSize(doc.file_size)} • {new Date(doc.created_at || doc.uploaded_at).toLocaleDateString(lang === "fr" ? "fr-FR" : "en-US")}
                            </p>
                          </div>
                          <div className="flex items-center gap-1 shrink-0 pt-1">
                            <a
                              href={`${API_BASE_URL}/documents/download/${doc.doc_id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-1.5 rounded-lg text-navy/70 hover:text-navy hover:bg-navy/10 transition"
                              title={lang === "fr" ? "Voir / Télécharger" : "View / Download"}
                            >
                              <Download size={14} />
                            </a>
                            <button
                              type="button"
                              onClick={() => handleDelete(doc.doc_id)}
                              disabled={deletingId === doc.doc_id}
                              className="p-1.5 rounded-lg text-red-500 hover:bg-red-50 transition"
                              title={lang === "fr" ? "Supprimer" : "Delete"}
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </div>

                        {/* Admin Feedback note if any */}
                        {doc.admin_notes && (
                          <div className="bg-amber-100/60 border border-amber-200 rounded-lg p-2 text-amber-900 text-[11px] flex items-start gap-1.5">
                            <Info size={13} className="shrink-0 text-amber-700 mt-0.5" />
                            <div>
                              <span className="font-semibold">{lang === "fr" ? "Note JNIC : " : "JNIC note: "}</span>
                              <span>{doc.admin_notes}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Action Upload Button */}
              <button
                type="button"
                onClick={() => handleOpenUpload(cat.id)}
                className="w-full mt-2 inline-flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl text-xs font-semibold bg-navy/5 hover:bg-navy text-navy hover:text-white transition-all border border-navy/10 group"
              >
                <UploadCloud size={14} className="text-terracotta group-hover:text-white transition-colors" />
                <span>
                  {catDocs.length > 0
                    ? lang === "fr"
                      ? "Ajouter un autre fichier pour ce type"
                      : "Add another file for this category"
                    : lang === "fr"
                    ? "Téléverser le document"
                    : "Upload document"}
                </span>
              </button>
            </motion.div>
          );
        })}
      </div>

      {/* Security & Regulatory Notice */}
      <div className="bg-alabaster border border-navy/10 rounded-2xl p-6 flex items-start gap-4">
        <div className="p-2.5 bg-terracotta/10 text-terracotta rounded-xl shrink-0">
          <ShieldCheck size={22} />
        </div>
        <div className="text-xs text-navy/80 space-y-1">
          <p className="font-bold text-navy text-sm">
            {familySituation === "single"
              ? lang === "fr" ? "Traitement Sécurisé de votre Dossier Individuel" : "Secure Individual File Processing"
              : lang === "fr" ? "Constitution Sécurisée du Dossier Familial" : "Secure Family File Organization"}
          </p>
          <p>
            {lang === "fr"
              ? "Vos pièces d'immigration sont stockées de façon confidentielle et chiffrée. Chaque fichier doit être clair, complet (toutes les pages) et au format PDF, JPG ou PNG (max 15 Mo par fichier)."
              : "Your immigration documents are stored securely with end-to-end access control. Please ensure uploads are high resolution and include all pages (up to 15 MB per file)."}
          </p>
        </div>
      </div>

      {/* Upload Modal with Adaptive Family Selector */}
      <AnimatePresence>
        {uploadModalCategory && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-navy/60 backdrop-blur-xs">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-3xl p-6 sm:p-8 max-w-lg w-full shadow-2xl border border-navy/10 relative max-h-[90vh] overflow-y-auto"
            >
              <button
                type="button"
                onClick={() => setUploadModalCategory(null)}
                className="absolute top-5 right-5 p-2 rounded-full text-navy/50 hover:text-navy hover:bg-navy/5 transition"
              >
                <X size={20} />
              </button>

              <div className="flex items-center gap-3 mb-4">
                <div className="p-2.5 rounded-xl bg-terracotta/10 text-terracotta">
                  <UploadCloud size={22} />
                </div>
                <div>
                  <h3 className="font-serif text-xl font-bold text-navy">
                    {lang === "fr" ? "Téléverser un document" : "Upload Document"}
                  </h3>
                  <p className="text-xs text-navy/60">
                    {lang === "fr"
                      ? ALL_CATEGORIES.find((c) => c.id === uploadModalCategory)?.fr
                      : ALL_CATEGORIES.find((c) => c.id === uploadModalCategory)?.en}
                  </p>
                </div>
              </div>

              <form onSubmit={handleUploadSubmit} className="space-y-4 mt-4">
                {/* 1. Family Member Selector (Only if Couple or Family) */}
                {familySituation !== "single" && (
                  <div>
                    <label className="block text-xs font-semibold text-navy uppercase tracking-wider mb-2">
                      {lang === "fr" ? "1. Pour qui est ce document ?" : "1. Whom is this document for?"}
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      {FAMILY_ROLES.filter((role) => (familySituation === "couple" ? role.id === "main_applicant" || role.id === "spouse" : true)).map((role) => {
                        const Icon = role.icon;
                        const isSelected = familyMember === role.id;
                        return (
                          <button
                            key={role.id}
                            type="button"
                            onClick={() => setFamilyMember(role.id)}
                            className={`flex items-center gap-2 p-2.5 rounded-xl border text-xs font-semibold transition text-left ${
                              isSelected
                                ? "border-terracotta bg-terracotta/5 text-navy ring-1 ring-terracotta"
                                : "border-navy/15 hover:border-navy/30 text-navy/70 bg-white"
                            }`}
                          >
                            <Icon size={16} className={isSelected ? "text-terracotta" : "text-navy/40"} />
                            <span className="truncate">{lang === "fr" ? role.fr : role.en}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Optional specific name for Child or Other */}
                {familySituation !== "single" && (familyMember === "child" || familyMember === "spouse" || familyMember === "other") && (
                  <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }}>
                    <label className="block text-xs font-semibold text-navy uppercase tracking-wider mb-1.5">
                      {familyMember === "child"
                        ? lang === "fr"
                          ? "Prénom de l'enfant (Ex: Lucas ou Enfant 1)"
                          : "Child's First Name (E.g. Lucas or Child 1)"
                        : familyMember === "spouse"
                        ? lang === "fr"
                          ? "Nom du conjoint (Optionnel)"
                          : "Spouse's Name (Optional)"
                        : lang === "fr"
                        ? "Précision sur le membre (Ex: Parent, Tuteur)"
                        : "Member clarification"}
                    </label>
                    <input
                      type="text"
                      value={familyMemberName}
                      onChange={(e) => setFamilyMemberName(e.target.value)}
                      placeholder={
                        familyMember === "child"
                          ? lang === "fr"
                            ? "Ex: Jean-Marc (Enfant 1)"
                            : "E.g. Marc (Child 1)"
                          : lang === "fr"
                          ? "Ex: Marie Dupont"
                          : "E.g. Marie Dupont"
                      }
                      className="w-full px-4 py-2 rounded-xl border border-navy/20 text-xs sm:text-sm focus:outline-hidden focus:border-terracotta"
                    />
                  </motion.div>
                )}

                {/* 2. Custom Title */}
                <div>
                  <label className="block text-xs font-semibold text-navy uppercase tracking-wider mb-1.5">
                    {familySituation === "single"
                      ? lang === "fr" ? "1. Titre / Description du document" : "1. Document Title / Description"
                      : lang === "fr" ? "2. Titre / Description du document" : "2. Document Title / Description"}
                  </label>
                  <input
                    type="text"
                    value={customTitle}
                    onChange={(e) => setCustomTitle(e.target.value)}
                    placeholder={
                      lang === "fr"
                        ? "Ex: Passeport Valide 2029 ou Relevés de notes Master"
                        : "E.g. Valid Passport 2029 or Master Transcripts"
                    }
                    className="w-full px-4 py-2.5 rounded-xl border border-navy/20 text-xs sm:text-sm focus:outline-hidden focus:border-terracotta"
                  />
                </div>

                {/* 3. Dropzone */}
                <div>
                  <label className="block text-xs font-semibold text-navy uppercase tracking-wider mb-1.5">
                    {familySituation === "single"
                      ? lang === "fr" ? "2. Fichier (PDF, JPG, PNG - Max 15 Mo)" : "2. File (PDF, JPG, PNG - Max 15 MB)"
                      : lang === "fr" ? "3. Fichier (PDF, JPG, PNG - Max 15 Mo)" : "3. File (PDF, JPG, PNG - Max 15 MB)"}
                  </label>
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    className={`border-2 border-dashed rounded-2xl p-6 text-center cursor-pointer transition-all ${
                      selectedFile
                        ? "border-emerald-500 bg-emerald-50/20"
                        : "border-navy/20 hover:border-terracotta hover:bg-alabaster"
                    }`}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx"
                      onChange={handleFileChange}
                      className="hidden"
                    />
                    <UploadCloud size={28} className={`mx-auto mb-2 ${selectedFile ? "text-emerald-600" : "text-navy/40"}`} />
                    {selectedFile ? (
                      <div>
                        <p className="text-sm font-semibold text-navy truncate">{selectedFile.name}</p>
                        <p className="text-xs text-emerald-600 font-medium mt-0.5">
                          {formatFileSize(selectedFile.size)} • {lang === "fr" ? "Prêt à envoyer" : "Ready to upload"}
                        </p>
                      </div>
                    ) : (
                      <div>
                        <p className="text-xs font-medium text-navy">
                          {lang === "fr" ? "Cliquez ou glissez-déposez votre fichier ici" : "Click or drag & drop your file here"}
                        </p>
                        <p className="text-[11px] text-navy/40 mt-1">PDF, JPG, PNG jusqu'à 15 Mo</p>
                      </div>
                    )}
                  </div>
                </div>

                {uploadError && (
                  <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-xs flex items-center gap-2">
                    <AlertTriangle size={16} className="shrink-0" />
                    <span>{uploadError}</span>
                  </div>
                )}

                {uploadSuccess && (
                  <div className="p-3 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs flex items-center gap-2">
                    <CheckCircle2 size={16} className="shrink-0" />
                    <span>{uploadSuccess}</span>
                  </div>
                )}

                <div className="flex items-center justify-end gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setUploadModalCategory(null)}
                    disabled={uploading}
                    className="px-4 py-2.5 rounded-xl text-xs font-semibold text-navy/70 hover:bg-navy/5 transition"
                  >
                    {lang === "fr" ? "Annuler" : "Cancel"}
                  </button>
                  <button
                    type="submit"
                    disabled={!selectedFile || uploading}
                    className="px-6 py-2.5 rounded-xl text-xs font-semibold bg-terracotta hover:bg-[#B91219] text-white transition disabled:opacity-50 flex items-center gap-2 shadow-sm"
                  >
                    {uploading && <Loader2 size={14} className="animate-spin" />}
                    <span>{uploading ? (lang === "fr" ? "Téléversement..." : "Uploading...") : lang === "fr" ? "Enregistrer le document" : "Save Document"}</span>
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
